import { FC, memo } from 'react'
import { NodeProps, Position } from '@xyflow/react'
import { useTranslate } from 'react-admin'
import Typography from '@mui/material/Typography'
import SearchIcon from '@mui/icons-material/Search'
import BaseNode from './BaseNode'
import { DataStoreReadNodeData } from '../types'
import { xxllncColor } from '../../../layout/theme/color'

const DataStoreReadNode: FC<NodeProps> = ({ id, data, selected }) => {
  const translate = useTranslate()
  const nodeData = data as DataStoreReadNodeData
  const identifier = (nodeData.identifier ?? '').trim()
  const isPlain = nodeData.storageMode === 'plain'
  const hasQuery = Boolean((nodeData.query ?? '').trim())
  const keyValue = (nodeData.key ?? '').trim()
  const summary = translate(
    isPlain ? 'workflow.node.dataStoreReadPlainSummary' : 'workflow.node.dataStoreReadVectorSummary',
  )
  const detail = isPlain
    ? keyValue || translate('workflow.node.dataStoreReadAll')
    : `top ${nodeData.topK ?? 5}`
  const subtitle = identifier
    ? `${summary} · ${identifier} · ${detail}`
    : translate('workflow.node.dataStoreMissingIdentifier')

  // Vector mode needs a query; plain mode does not (an empty key returns all
  // entries in the identifier, which is a valid use case for "list everything").
  const showError = !identifier || (!isPlain && !hasQuery)
  const errorText = !identifier
    ? translate('workflow.node.dataStoreMissingIdentifier')
    : translate('workflow.node.dataStoreMissingQuery')

  return (
    <BaseNode
      // eslint-disable-next-line camelcase
      nodeType="data_store_read"
      icon={<SearchIcon fontSize="small" />}
      label={nodeData.label}
      nodeId={id}
      subtitle={subtitle}
      selected={selected}
      handles={[
        { type: 'target', position: Position.Top },
        { type: 'source', position: Position.Bottom },
      ]}
    >
      {showError && (
        <Typography
          variant="caption"
          sx={{
            color: xxllncColor.orange100,
            fontWeight: 600,
            display: 'block',
            lineHeight: 1.35,
          }}
        >
          {errorText}
        </Typography>
      )}
    </BaseNode>
  )
}

export default memo(DataStoreReadNode)
